<template>
  <div class="heroes__main">
    <div
      ref="block"
      class="heroes__block"
      :class="{ 'font-loaded': fontLoaded }"
      :style="blockStyle"
    >
      <NestedComponents 
        :componentList="landingData" 
        :readonly="readonly" 
        :jsoneditor="jsoneditor"
      />


      <transition name="fade">
        <div v-if="loading" class="font-loader-overlay" aria-hidden="true">
          <div class="loader-inner">
            <div class="spinner"></div>
            <div class="loader-text">Загружаем...</div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import NestedComponents from '@/components/Nested/NestedComponents.vue'
import { loadFontsFromItems, loadFontOnce } from '@/utils/font-loader.js';

export default {
  name: 'HeroesBlock',
  components: { NestedComponents },
  props: {
    readonly: {
      type: Boolean,
      default: false
    },
    jsoneditor: {
      type: Boolean,
      default: false
    },
    previewLandingData: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      loading: true,               // overlay видим по умолчанию — будем держать его до завершения загрузки всех шрифтов
      fontLoaded: false,           // флаг, что основной шрифт блока загружен
      currentFontCssValue: '',
      styleEl: null,
      fontsPreloadStarted: false,
      allElementFontsLoaded: false
    }
  },
  computed: {
    landingSettings() {
      return this.$store.getters['landing/landingSettings'] || { fonts: [], bg_color: 'transparent' }
    },
    landingData() {
      if(this.previewLandingData) return this.previewLandingData
      return this.$store.getters['landing/landingData'] || []
    },
    fallbackStack() {
      return `system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`
    },
    blockStyle() {
      const fontValue = this.currentFontCssValue || this.fallbackStack;
      const bg = this.landingSettings.bg_color ?? 'transparent';
      return {
        '--current-font': fontValue,
        background: bg,
      };
    }
  },
  async mounted() {
    // Блокирующая загрузка: ждём одновременно и основной шрифт лендинга, и шрифты всех элементов
    await this.loadAllBlocking();
  },
  watch: {
    'landingSettings.fonts': {
      handler() {
        // если сменился основной шрифт в настройках — перезагружаем (блокирующе)
        this.loadAllBlocking().catch(()=>{});
      },
      deep: true
    },
    'landingSettings.bg_color': {
      handler() {
        // ничего не делаем специально (фон применяется через blockStyle)
      }
    },
    landingData: {
      handler() {
        // если контент лендинга изменился — запустим заново предзагрузку шрифтов (блокирующе)
        this.loadAllBlocking().catch(()=>{});
      },
      deep: true
    }
  },
  methods: {
    /**
     * Блокирующая последовательность: держим overlay (=loading) пока загрузятся
     *  - основной шрифт лендинга (loadAndApplyFont)
     *  - и все шрифты из элементов (preloadElementFonts({blockUntilDone:true}))
     */
    async loadAllBlocking() {
      // защитимся от SSR
      if (typeof window === 'undefined') return;

      // ставим overlay
      this.loading = true;
      this.fontLoaded = false;
      this.allElementFontsLoaded = false;

      try {
        // Запускаем обе задачи параллельно и ждём обе
        const tasks = [
          this.loadAndApplyFont(), // возвращает true/false по результату загрузки основного шрифта
          this.preloadElementFonts({ blockUntilDone: true }) // ждём предзагрузку шрифтов элементов
        ];

        const [primaryLoaded] = await Promise.all(tasks);

        // Немного задержим смену состояния, чтобы избежать дергания UI (как и раньше)
        if (primaryLoaded) {
          // если основной шрифт загрузился — применим его видимо и покажем плавно
          try { await new Promise(res => setTimeout(res, 80)); } catch(e){}
          this.fontLoaded = true;
          try { await new Promise(res => setTimeout(res, 200)); } catch(e){}
        } else {
          // если основной шрифт не загрузился — используем fallback, но всё равно убираем overlay через небольшой таймаут
          this.fontLoaded = false;
          try { await new Promise(res => setTimeout(res, 120)); } catch(e){}
        }
      } catch (err) {
        // в случае ошибок — просто логируем и убираем overlay
        console.warn('loadAllBlocking error', err);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Загружает и применяет основной шрифт лендинга (landingSettings.fonts[0]).
     * Возвращает true если шрифт успешно загружен/подключён, false иначе.
     * НЕ управляет overlay — управление loading происходит в loadAllBlocking.
     */
    async loadAndApplyFont() {
      const font = this.landingSettings.fonts?.[0];
      this.currentFontCssValue = '';
      this.fontLoaded = false;

      if (!font || !font.files && !font.url) {
        // нет данных по шрифту — используем fallback
        this.currentFontCssValue = this.fallbackStack;
        return false;
      }

      // выбираем наиболее подходящий URL (как было в оригинале)
      const prefer = ['regular', '400', 'woff2', 'woff', 'truetype', 'ttf'];
      let url = null;
      for (const k of prefer) {
        if (font.files && font.files[k]) { url = font.files[k]; break; }
      }
      if (!url && font.files) {
        const keys = Object.keys(font.files || {});
        url = keys.length ? font.files[keys[0]] : null;
      }
      if (!url && font.url) url = font.url;

      if (!url) {
        console.error('No usable font URL found for', font);
        this.currentFontCssValue = this.fallbackStack;
        return false;
      }

      // inject @font-face into head (so css var will apply instantly if browser uses it)
      try {
        if (this.styleEl && document.head.contains(this.styleEl)) {
          document.head.removeChild(this.styleEl);
          this.styleEl = null;
        }
        const familyEsc = font.family.replace(/"/g, '\\"');
        this.styleEl = document.createElement('style');
        this.styleEl.type = 'text/css';
        let fmt = 'truetype';
        if (url.endsWith('.woff2')) fmt = 'woff2';
        else if (url.endsWith('.woff')) fmt = 'woff';
        else if (url.endsWith('.ttf') || url.endsWith('.otf')) fmt = 'truetype';
        this.styleEl.textContent = `
@font-face {
  font-family: "${familyEsc}";
  src: url("${url}") format("${fmt}");
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}
`;
        document.head.appendChild(this.styleEl);
      } catch (e) {
        console.warn('Could not append @font-face style:', e);
      }

      // Попытка через FontFace API (при успехе возвращаем true)
      let loaded = false;
      try {
        if (window.FontFace) {
          const ff = new FontFace(font.family, `url(${url})`, { weight: '400', style: 'normal' });
          await ff.load();
          document.fonts.add(ff);
          loaded = true;
        }
      } catch (err) {
        console.warn('FontFace API failed:', err);
      }

      // Fallback через document.fonts.load
      if (!loaded && document.fonts && document.fonts.load) {
        try {
          const loadPromise = document.fonts.load(`1rem "${font.family}"`);
          const timeout = new Promise(res => setTimeout(res, 4000));
          await Promise.race([loadPromise, timeout]);
          if (document.fonts.check(`1rem "${font.family}"`)) {
            loaded = true;
          } else {
            console.warn('document.fonts.load did not finish in time for', font.family);
          }
        } catch (e) {
          console.warn('document.fonts.load error', e);
        }
      }

      // если загружено — установим CSS-переменную
      if (loaded || (document.fonts && document.fonts.check && document.fonts.check(`1rem "${font.family}"`))) {
        this.currentFontCssValue = `"${font.family}", ${this.fallbackStack}`;
        return true;
      } else {
        // не загрузилось — используем fallback
        this.currentFontCssValue = this.fallbackStack;
        return false;
      }
    },

    /**
     * Предзагрузка всех шрифтов из элементов.
     * Если blockUntilDone === true — функция вернёт промис, который резолвится после завершения загрузки всех шрифтов.
     * Если false — запустит загрузку в фоне и вернёт немедленно.
     */
    async preloadElementFonts({ blockUntilDone = false } = {}) {
      if (typeof window === 'undefined') return;
      if (this.fontsPreloadStarted && !blockUntilDone) return;

      this.fontsPreloadStarted = true;
      const items = this.previewLandingData ?? (this.$store.getters['landing/landingData'] || []);

      if (!Array.isArray(items) || items.length === 0) {
        this.allElementFontsLoaded = true;
        return;
      }

      const promise = loadFontsFromItems(items)
        .then(() => { this.allElementFontsLoaded = true; })
        .catch(() => { this.allElementFontsLoaded = true; });

      if (blockUntilDone) {
        await promise;
      } else {
        // не ждём — фон
        promise.catch(()=>{});
      }
    }
  }
}
</script>


<style scoped>
:root {
  --fallback-stack: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/* мы используем CSS-переменную --current-font; по умолчанию она равна fallback */
.heroes__block {
  position: relative;
  font-family: var(--current-font, var(--fallback-stack));
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 7.5rem; 
  width: 100%;       
  max-width: 100%;   
  box-sizing: border-box;
  height: 100%;
  overflow: hidden;
  max-height: 100%; 
}



.heroes__block {
  font-family: var(--current-font, var(--fallback-stack));
  color: inherit; 
}

/* оверлей поверх содержимого */
.font-loader-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  pointer-events: auto;
  backdrop-filter: blur(0.1875rem); /* 3px -> 0.1875rem (optional) */
}

/* внутренний контейнер лоадера */
.loader-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem; /* 12px -> 0.75rem */
}

/* спиннер */
.spinner {
  width: 3rem;        /* 48px -> 3rem */
  height: 3rem;       /* 48px -> 3rem */
  border-radius: 50%;
  border: 0.25rem solid rgba(0,0,0,0.08); /* 4px -> 0.25rem */
  border-top-color: #3b82f6;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.loader-text {
  font-weight: 600;
  color: var(--color-text);
  font-size: 1rem; 
}

/* плавное появление/исчезновение overlay */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.35s ease;
}
.fade-enter-from, .fade-leave-to { opacity: 0; }
.fade-enter-to, .fade-leave-from { opacity: 1; }
.heroes__main {
  width: 100%;
}
.heroes__block {
  width: 100%;
}
</style>

